<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import type { Device } from 'src/types/Device';
	import type { CloudRecordingDayOverview } from 'src/components/Camera/types/cloud-recordings';
	import { devices_status } from 'src/stores';
	import { camera_storage_keys } from 'src/components/Camera/stores/camera_storage_keys';
	import { camera_setup_prompt } from 'src/components/Camera/stores/camera_setup_prompt';
	import RecordingSetupPrompt from 'src/components/Camera/CameraPlayer/RecordingSetupPrompt.svelte';
	import {
		AdvancedCardV2,
		ButtonV2,
		Datepicker,
		Icon,
		LabelListV2,
		Loader,
		Modal,
		Text,
		toast,
	} from '@Shelly/shelly-design-system/index';
	import {
		deleteDeviceRecordings,
		getCameraStorageKeys,
		getDeviceRecordingsOverview,
	} from 'src/services/cam-cloud/recordings';
	import { isCameraEncryptionSupported } from 'src/components/Camera/helpers/camera-features';
	import { real_id } from 'src/helpers/interface_helpers';

	export let device: Device;

	let showDeleteAllModal = false;
	let showDeleteByDateModal = false;
	let deleteAllLoading = false;
	let deleteByDateLoading = false;
	let selectedDate: string | null = null;

	let showRotateConfirmModal = false;

	let overviewDays: CloudRecordingDayOverview[] = [];
	let overviewLoading = true;

	/** Whether the cloud reports a stored key backup for this device. */
	let cloudHasKey = false;

	// Normalised device id; the storage API refers to this same value as `mac`.
	$: deviceId = real_id(device?.id);

	/** Whether the firmware supports creating/rotating a recording encryption key. */
	$: encryptionSupported = isCameraEncryptionSupported(device, $devices_status[deviceId]);

	// A device is "encrypting" when a key backup exists locally or in the cloud.
	$: localHasKey = ($camera_storage_keys[deviceId]?.length ?? 0) > 0;
	$: hasKey = localHasKey || cloudHasKey;

	$: totalDays = overviewDays.length;
	$: totalVideos = overviewDays.reduce((sum, day) => sum + (day.videos ?? 0), 0);

	$: overviewLabels = overviewLoading
		? []
		: [
				{
					id: 'videos',
					icon: 'fal fa-video',
					value: $format('camera_cloud_overview_videos', {
						values: { count: totalVideos },
						default: `${totalVideos} videos`,
					}),
				},
				{
					id: 'days',
					icon: 'fal fa-calendar-day',
					value: $format('camera_cloud_overview_days', {
						values: { count: totalDays },
						default: `${totalDays} days`,
					}),
				},
			];

	const loadOverview = async () => {
		if (!deviceId) return;
		overviewLoading = true;
		try {
			const { days } = await getDeviceRecordingsOverview(deviceId);
			overviewDays = days ?? [];
		} catch (error) {
			console.log('Failed to load recordings overview', error);
		} finally {
			overviewLoading = false;
		}
	};

	/** Best-effort cloud check so the encryption state reflects other devices/logins. */
	const loadKeyState = async () => {
		if (!deviceId) return;
		try {
			const entries = await getCameraStorageKeys(deviceId);
			cloudHasKey = entries.some((entry) => entry?.blob && entry?.hmac);
		} catch (error) {
			console.log('Failed to load camera key state', error);
		}
	};

	const showResultToast = (deleted: number) => {
		toast.open({
			type: 'success',
			title: $format('camera_cloud_recordings_deleted_toast', {
				values: { count: deleted },
				default: `Deleted ${deleted} recordings.`,
			}),
		});
	};

	const showErrorToast = () => {
		toast.open({
			type: 'danger',
			title: $format('camera_cloud_recordings_delete_error', {
				default: 'Could not delete recordings. Please retry.',
			}),
		});
	};

	const onDeleteAll = async () => {
		deleteAllLoading = true;
		try {
			const { deleted } = await deleteDeviceRecordings(deviceId);
			showResultToast(deleted);
			showDeleteAllModal = false;
			await loadOverview();
		} catch (error) {
			console.log('Failed to delete recordings', error);
			showErrorToast();
		}
		deleteAllLoading = false;
	};

	const onGenerateKey = () => {
		camera_setup_prompt.open(deviceId);
	};

	const onRotateKey = () => {
		showRotateConfirmModal = true;
	};

	const onConfirmRotate = () => {
		showRotateConfirmModal = false;
		onGenerateKey();
	};

	const handleDateChange = (event: CustomEvent) => {
		selectedDate = event.detail?.[1] ?? null;
	};

	const onDeleteByDate = async () => {
		if (!selectedDate) return;
		deleteByDateLoading = true;
		try {
			const { deleted } = await deleteDeviceRecordings(deviceId, selectedDate);
			showResultToast(deleted);
			showDeleteByDateModal = false;
			selectedDate = null;
			await loadOverview();
		} catch (error) {
			console.log('Failed to delete recordings', error);
			showErrorToast();
		}
		deleteByDateLoading = false;
	};

	onMount(() => {
		loadOverview();
		loadKeyState();
	});
</script>

<AdvancedCardV2
	class="mb-s"
	aria-label={$format('camera_cloud_overview_title', { default: 'Camera Cloud Storage' })}
	title={$format('camera_cloud_overview_title', { default: 'Camera Cloud Storage' })}
	avatar={{ icon: 'fal fa-camera-web', backgroundColor: 'gradient-pink-purple' }}
>
	<LabelListV2 slot="main" size="s" labelList={overviewLabels} a11yProps={{ isInteractive: false }} />
	<div slot="right" class="d-flex align-items-center">
		{#if overviewLoading}
			<Loader size="m" />
		{:else if hasKey}
			<Icon
				size="m"
				icon="fal fa-shield-check"
				class="text-success"
				aria-label={$format('camera_cloud_overview_encrypted', {
					default: 'Recordings are encrypted',
				})}
			/>
		{:else}
			<Icon
				size="m"
				icon="fal fa-cloud-check"
				class="text-success"
				aria-label={$format('camera_cloud_overview_secured', {
					default: 'Recordings are securely stored',
				})}
			/>
		{/if}
	</div>
</AdvancedCardV2>

<AdvancedCardV2
	class="mb-s"
	aria-label={$format('camera_cloud_generate_key_title', { default: 'Recording encryption' })}
	title={$format('camera_cloud_generate_key_title', { default: 'Recording encryption' })}
	avatar={{ icon: hasKey ? 'fal fa-shield-check' : 'fal fa-key' }}
	subtitle={{
		type: 'muted',
		bold: false,
		textSmall: true,
		value: $format('camera_cloud_encryption_desc', {
			default:
				"Encrypted with a key only you can unlock with your password. It can't be recovered if lost.",
		}),
	}}
>
	<div slot="right" class="d-flex align-items-center">
		{#if hasKey}
			<Icon
				size="s"
				icon="fal fa-shield-check"
				class="text-success mr-xs"
				aria-label={$format('camera_cloud_overview_encrypted', {
					default: 'Recordings are encrypted',
				})}
			/>
			<Text styleType="body-small">
				{$format('camera_cloud_encryption_encrypted', { default: 'Encrypted' })}
			</Text>
		{:else}
			<ButtonV2
				size="s"
				variant="secondary"
				disabled={!encryptionSupported}
				on:click={onGenerateKey}
				aria-label={$format('camera_cloud_encryption_start', {
					default: 'Start Encrypting',
				})}
			>
				{$format('camera_cloud_encryption_start', { default: 'Start Encrypting' })}
			</ButtonV2>
		{/if}
	</div>
</AdvancedCardV2>

{#if hasKey}
	<AdvancedCardV2
		class="mb-s"
		aria-label={$format('camera_cloud_rotate_title', { default: 'Rotate encryption key' })}
		title={$format('camera_cloud_rotate_title', { default: 'Rotate encryption key' })}
		avatar={{ icon: 'fal fa-arrows-rotate' }}
		subtitle={{
			type: 'muted',
			bold: false,
			textSmall: true,
			value: $format('camera_cloud_rotate_desc', {
				default: 'Generate a new key for future recordings',
			}),
		}}
	>
		<div slot="right" class="d-flex align-items-center">
			<ButtonV2
				size="s"
				variant="secondary"
				disabled={!encryptionSupported}
				on:click={onRotateKey}
				aria-label={$format('camera_cloud_rotate_button', { default: 'Rotate' })}
			>
				{$format('camera_cloud_rotate_button', { default: 'Rotate' })}
			</ButtonV2>
		</div>
	</AdvancedCardV2>
{/if}

<AdvancedCardV2
	class="mb-s"
	aria-label={$format('camera_cloud_delete_by_date_title', { default: 'Delete recordings by date' })}
	title={$format('camera_cloud_delete_by_date_title', { default: 'Delete recordings by date' })}
	avatar={{ icon: 'fal fa-calendar-day' }}
	subtitle={{
		type: 'muted',
		bold: false,
		textSmall: true,
		value: $format('camera_cloud_delete_by_date_desc', {
			default: "Remove a day's recordings",
		}),
	}}
>
	<div slot="right" class="d-flex align-items-center">
		<ButtonV2
			size="s"
			variant="secondary"
			on:click={() => (showDeleteByDateModal = true)}
			aria-label={$format('button_delete', { default: 'Delete' })}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</AdvancedCardV2>

<AdvancedCardV2
	aria-label={$format('camera_cloud_delete_all_title', { default: 'Delete all recordings' })}
	title={$format('camera_cloud_delete_all_title', { default: 'Delete all recordings' })}
	avatar={{ icon: 'fal fa-trash' }}
	subtitle={{
		type: 'muted',
		bold: false,
		textSmall: true,
		value: $format('camera_cloud_delete_all_desc', {
			default: 'Delete all cloud recordings',
		}),
	}}
>
	<div slot="right" class="d-flex align-items-center">
		<ButtonV2
			size="s"
			variant="danger"
			on:click={() => (showDeleteAllModal = true)}
			aria-label={$format('button_delete', { default: 'Delete' })}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</AdvancedCardV2>

<Modal
	showModal={showDeleteAllModal}
	title={$format('camera_cloud_delete_all_confirm_title', { default: 'Delete all recordings?' })}
	backdropClose={false}
	on:close={() => (showDeleteAllModal = false)}
>
	<svelte:fragment slot="content">
		<Text>
			{$format('camera_cloud_delete_all_confirm_message', {
				default:
					'This will permanently delete every cloud recording for this camera. This action cannot be undone.',
			})}
		</Text>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			class="mr-s"
			variant="secondary"
			disabled={deleteAllLoading}
			on:click={() => (showDeleteAllModal = false)}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="danger"
			loading={deleteAllLoading}
			on:click={onDeleteAll}
			aria-label={$format('button_delete', { default: 'Delete' })}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</Modal>

<Modal
	showModal={showDeleteByDateModal}
	title={$format('camera_cloud_delete_by_date_modal_title', { default: 'Delete recordings by date' })}
	backdropClose={false}
	on:close={() => (showDeleteByDateModal = false)}
>
	<svelte:fragment slot="content">
		<Datepicker
			id="camera-cloud-delete-date"
			aria-label={$format('camera_cloud_delete_by_date_picker', { default: 'Select date' })}
			a11yProps={{
				inputAriaLabel: 'date input',
				selectMonthAriaLabel: 'select month',
				selectYearAriaLabel: 'select year',
			}}
			maxDate={new Date()}
			label={$format('camera_cloud_delete_by_date_picker', { default: 'Select date' })}
			on:change={handleDateChange}
		/>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			class="mr-s"
			variant="secondary"
			disabled={deleteByDateLoading}
			on:click={() => (showDeleteByDateModal = false)}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="danger"
			loading={deleteByDateLoading}
			disabled={!selectedDate || deleteByDateLoading}
			on:click={onDeleteByDate}
			aria-label={$format('button_delete', { default: 'Delete' })}
		>
			{$format('button_delete', { default: 'Delete' })}
		</ButtonV2>
	</div>
</Modal>

<Modal
	showModal={showRotateConfirmModal}
	title={$format('camera_cloud_rotate_confirm_title', { default: 'Rotate encryption key?' })}
	backdropClose={false}
	on:close={() => (showRotateConfirmModal = false)}
>
	<svelte:fragment slot="content">
		<Text>
			{$format('camera_cloud_rotate_confirm_message', {
				default:
					'All new recordings will be encrypted with a new key. Existing recordings stay decryptable with their original password. Continue?',
			})}
		</Text>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			class="mr-s"
			variant="secondary"
			on:click={() => (showRotateConfirmModal = false)}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			variant="primary"
			on:click={onConfirmRotate}
			aria-label={$format('camera_cloud_rotate_confirm_button', { default: 'Rotate key' })}
		>
			{$format('camera_cloud_rotate_confirm_button', { default: 'Rotate key' })}
		</ButtonV2>
	</div>
</Modal>

<RecordingSetupPrompt {device} />
